/* Wynwood dog profile. ONE OWNER of the shared bus.

   Every surface that knows something about the customer's dog reads and
   writes it through here. Nothing else touches localStorage.

   WHY THIS EXISTS. Each surface used to write the whole stored object
   wholesale, so any field the writing surface did not know about was
   destroyed on the next save. That is why spay/neuter never survived a trip
   from the calculator page to a product page. set() MERGES, so a surface can
   save a weight without deleting a field it has never heard of.

   TWO KEYS, DELIBERATELY, AND THEY HOLD DISJOINT FIELDS.
     wyn_dog    the legacy five: weight_lb, age, activity, portion, updated.
                Untouched in shape, because un-migrated surfaces still read
                and wholesale-write it. This is the interop contract.
     wyn_dog_x  everything else: neuter, ageMonths, activityWord, name,
                source. A legacy wholesale write to wyn_dog CANNOT reach it,
                so new fields survive even while surfaces are still being
                migrated one at a time.
   The two never hold the same field, so there is no conflict to resolve and
   no second source of truth. When every surface is migrated the extended key
   can be folded in and this comment deleted.

   No DOM beyond the event dispatch. No fetch. No feeding math.

   BIRTHDAYS (2026-11-01). wyn_dog_x also holds `bday`, the dog's birthday in
   the hub's shapes: 2020-03-03, 2020-03, or ~2020-03 / ~~2020-03 for an estimate
   (one mark: ours; two: the owner's - same age either way). WHEN A
   BIRTHDAY IS PRESENT IT DECIDES THE AGE, EVERYWHERE, EVERY PAGE LOAD: get()
   works the months out from today's date, and refresh() below writes the
   matching factor into wyn_dog so surfaces that read the legacy key directly
   (the cart drawer, older calculators) see it too. A dog therefore moves from
   puppy to adult to senior on its own, with no visit to the account hub.
   An age tapped on another page cannot override a birthday here; that page's
   own figure follows the tap, but the shared record stays with the birthday.
*/
(function () {
  'use strict';

  var KEY = 'wyn_dog';
  var XKEY = 'wyn_dog_x';
  var EVT = 'wyn:dog';

  /* Factors, mirrored from wynfeed.js CFG. Kept here as plain lookups so the
     profile module does not depend on the math module loading first. If these
     ever disagree, wynfeed.js is right and this is wrong. */
  var ACT_WORD = { low: '1.2', normal: '1.55', high: '1.75' };
  var ACT_FACTOR = { '1.2': 'low', '1.55': 'normal', '1.75': 'high' };
  var NEUTER = { neutered: 1, intact: 1.125 };

  var AGE_BUCKETS = [
    { max: 3, factor: '2' },
    { max: 6, factor: '1.5' },
    { max: 12, factor: '1.25' },
    { max: 84, factor: '1' }
  ];
  var AGE_OLD = '0.9';

  function ageFactorFromMonths(months) {
    var m = Number(months);
    if (!isFinite(m) || m < 0) { return null; }
    for (var i = 0; i < AGE_BUCKETS.length; i++) {
      if (m < AGE_BUCKETS[i].max) { return AGE_BUCKETS[i].factor; }
    }
    return AGE_OLD;
  }

  /* ----------------------------------------------------------- birthdays */

  var BD_RE = /^(~{1,2})?(\d{4})-(\d{2})(?:-(\d{2}))?$/;
  function bday(v) {
    var m = BD_RE.exec(String(v == null ? '' : v).trim());
    if (!m) { return null; }
    var y = +m[2], mo = +m[3], d = m[4] ? +m[4] : 0;
    if (mo < 1 || mo > 12 || y < 1980 || (m[1] && d)) { return null; }
    if (d && (d < 1 || d > new Date(y, mo, 0).getDate())) { return null; }
    return m[0];
  }
  /* Same counting as the hub: from the day; a month-only birthday from the
     15th; an estimate from the 1st, because the owner gave whole months. */
  function monthsFromBday(b) {
    var m = BD_RE.exec(b || '');
    if (!m) { return null; }
    var born = new Date(+m[2], +m[3] - 1, m[4] ? +m[4] : (m[1] ? 1 : 15));
    var mo = (Date.now() - born.getTime()) / (30.4375 * 86400000);
    return Math.max(0, Math.round(mo * 10) / 10);
  }

  /* ------------------------------------------------------------- storage */

  function readKey(k) {
    try {
      var raw = window.localStorage.getItem(k);
      if (!raw) { return {}; }
      var o = JSON.parse(raw);
      return (o && typeof o === 'object' && !Array.isArray(o)) ? o : {};
    } catch (e) { return {}; }
  }

  function writeKey(k, obj) {
    try {
      window.localStorage.setItem(k, JSON.stringify(obj));
      return true;
    } catch (e) { return false; }
  }

  /* -------------------------------------------------------- normalising */

  function num(v) {
    var n = Number(v);
    return (isFinite(n) && n > 0) ? n : null;
  }

  /* Weight is the one field with a sane range. 2 to 250 lb matches the
     calculator slider; anything outside it is a typo or a stale value and is
     treated as absent rather than trusted. */
  function weight(v) {
    var n = num(v);
    if (n === null || n < 2 || n > 250) { return null; }
    return Math.round(n * 2) / 2;
  }

  function neuter(v) {
    var s = String(v == null ? '' : v).trim().toLowerCase();
    if (NEUTER[s]) { return s; }
    if (s === 'true' || s === 'yes' || s === 'spayed') { return 'neutered'; }
    if (s === 'false' || s === 'no') { return 'intact'; }
    var n = Number(v);
    if (n === 1) { return 'neutered'; }
    if (n === 1.125) { return 'intact'; }
    return null;
  }

  function activity(v) {
    var s = String(v == null ? '' : v).trim().toLowerCase();
    if (ACT_WORD[s]) { return ACT_WORD[s]; }
    var n = num(v);
    if (n !== null) { return String(n); }
    return null;
  }

  /* --------------------------------------------------------------- read */

  /* Always returns an object. Absent fields are null, never undefined, so a
     caller can test one way and never has to guess. */
  function get() {
    var base = readKey(KEY);
    var ext = readKey(XKEY);

    var bd = bday(ext.bday);
    var months = bd ? monthsFromBday(bd) : num(ext.ageMonths);
    var age = base.age != null ? String(base.age) : null;
    if (bd && months !== null) { age = ageFactorFromMonths(months); }
    else if (age === null && months !== null) { age = ageFactorFromMonths(months); }

    var act = activity(base.activity);
    var actWord = ext.activityWord || (act !== null ? ACT_FACTOR[act] : null) || null;

    return {
      weight_lb: weight(base.weight_lb),
      age: age,
      ageMonths: months,
      activity: act,
      activityWord: actWord,
      neuter: neuter(ext.neuter),
      bday: bd,
      portion: num(base.portion) || null,
      name: (ext.name != null && String(ext.name).trim()) ? String(ext.name).trim() : null,
      source: ext.source || null,
      updated: num(base.updated) || null,
      known: weight(base.weight_lb) !== null
    };
  }

  /* A profile object shaped for wynFeed.plan(). Only fields we actually hold
     are included, so the library applies its own defaults for the rest rather
     than being handed a guess dressed up as an answer. */
  function toPlan() {
    var p = get();
    var out = {};
    if (p.weight_lb !== null) { out.weight_lb = p.weight_lb; }
    if (p.ageMonths !== null) { out.ageMonths = p.ageMonths; }
    else if (p.age !== null) { out.ageFactor = Number(p.age); }
    if (p.activity !== null) { out.activity = Number(p.activity); }
    if (p.neuter !== null) { out.neuter = p.neuter; }
    return out;
  }

  /* -------------------------------------------------------------- write */

  /* MERGE, never replace. Pass only what you know. Pass null to clear one
     field. Returns the new full profile. */
  function set(patch, opts) {
    patch = patch || {};
    opts = opts || {};

    var base = readKey(KEY);
    var ext = readKey(XKEY);
    var touched = false;

    function put(store, field, value) {
      if (value === undefined) { return; }
      if (value === null) {
        if (store[field] !== undefined) { delete store[field]; touched = true; }
        return;
      }
      if (store[field] !== value) { store[field] = value; touched = true; }
    }

    if ('weight_lb' in patch) { put(base, 'weight_lb', weight(patch.weight_lb)); }
    if ('portion' in patch) { put(base, 'portion', num(patch.portion)); }

    /* Age accepts either months or a bare factor. Months is the better
       answer, so when both arrive months wins and the factor is derived. */
    if ('ageMonths' in patch) {
      var m = num(patch.ageMonths);
      put(ext, 'ageMonths', m);
      if (m !== null) { put(base, 'age', ageFactorFromMonths(m)); }
    }
    /* A caller setting a bare factor is answering at COARSER granularity than
       months, so any months we were holding is now stale and would otherwise
       win in toPlan() and silently override the answer just given. */
    if ('age' in patch && !('ageMonths' in patch)) {
      put(base, 'age', patch.age == null ? null : String(patch.age));
      put(ext, 'ageMonths', null);
    }

    /* A birthday owns the age: the months are worked out from it on every
       read, so any stored months would only be a stale second answer. */
    if ('bday' in patch) {
      var b = bday(patch.bday);
      put(ext, 'bday', b);
      if (b) {
        put(ext, 'ageMonths', null);
        put(base, 'age', ageFactorFromMonths(monthsFromBday(b)));
      }
    }

    if ('activity' in patch) {
      var a = activity(patch.activity);
      put(base, 'activity', a);
      put(ext, 'activityWord', a !== null ? (ACT_FACTOR[a] || null) : null);
    }

    if ('neuter' in patch) { put(ext, 'neuter', neuter(patch.neuter)); }
    if ('name' in patch) {
      put(ext, 'name', (patch.name != null && String(patch.name).trim())
        ? String(patch.name).trim() : null);
    }
    if ('source' in patch) { put(ext, 'source', patch.source ? String(patch.source) : null); }

    if (!touched && !opts.force) { return get(); }

    base.updated = Date.now();
    writeKey(KEY, base);
    writeKey(XKEY, ext);

    var full = get();
    if (!opts.silent) { announce(full); }
    return full;
  }

  function clear() {
    try {
      window.localStorage.removeItem(KEY);
      window.localStorage.removeItem(XKEY);
    } catch (e) {}
    announce(get());
  }

  /* --------------------------------------------------------------- bus */

  /* DISPATCH on documentElement, LISTEN on document. Events bubble upward, so
     dispatching low is heard at both levels, and listening high catches both
     our own broadcasts and the product engines, which dispatch on document.
     The other way round misses half the site. */
  function announce(detail) {
    try {
      document.documentElement.dispatchEvent(
        new CustomEvent(EVT, { detail: detail, bubbles: true })
      );
    } catch (e) {}
  }

  function subscribe(fn) {
    if (typeof fn !== 'function') { return function () {}; }
    var h = function (e) { fn((e && e.detail) || get(), e); };
    document.addEventListener(EVT, h);
    return function () { document.removeEventListener(EVT, h); };
  }

  /* Every page load: a dog with a birthday gets today's age written into the
     legacy key, silently, so direct readers of wyn_dog are never a band behind. */
  function refresh() {
    var ext = readKey(XKEY), b = bday(ext.bday);
    if (!b) { return; }
    var base = readKey(KEY), f = ageFactorFromMonths(monthsFromBday(b));
    if (f !== null && String(base.age) !== f) { base.age = f; writeKey(KEY, base); }
  }
  refresh();

  window.wynProfile = {
    KEY: KEY,
    XKEY: XKEY,
    EVENT: EVT,
    get: get,
    set: set,
    clear: clear,
    toPlan: toPlan,
    subscribe: subscribe,
    ageFactorFromMonths: ageFactorFromMonths,
    monthsFromBday: monthsFromBday
  };
})();